
import { useState, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface LinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  editor: Editor;
}

export default function LinkDialog({ isOpen, onClose, editor }: LinkDialogProps) {
  const [linkUrl, setLinkUrl] = useState('');
  
  // Set initial value when dialog opens
  useEffect(() => {
    if (isOpen && editor.isActive('link')) {
      const attrs = editor.getAttributes('link');
      setLinkUrl(attrs.href || '');
    } else if (isOpen) {
      setLinkUrl('');
    }
  }, [isOpen, editor]);
  
  const handleSetLink = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    if (!editor) return;
    
    if (linkUrl === '') {
      // Remove link by selecting it and unsetting the mark
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // Update link with the correct command
    editor.chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: linkUrl })
      .run();
    
    // Close dialog and reset url
    onClose();
    setLinkUrl('');
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="absolute top-full left-0 mt-1 p-2 bg-black/80 backdrop-blur-sm border border-white/10 rounded-md z-10 w-64">
      <div className="flex gap-2 mb-2">
        <Input
          type="url"
          placeholder="https://example.com"
          value={linkUrl}
          onChange={e => setLinkUrl(e.target.value)}
          className="flex-1 h-8 text-sm"
        />
      </div>
      <div className="flex justify-between">
        <Button 
          size="sm" 
          variant="ghost" 
          className="text-xs" 
          onClick={(e) => {
            e.preventDefault(); // Prevent form submission
            onClose();
          }}
          type="button" // Explicitly set type to button
        >
          Cancel
        </Button>
        <Button 
          size="sm" 
          variant={linkUrl ? 'default' : 'destructive'} 
          className="text-xs" 
          onClick={handleSetLink}
          type="button" // Explicitly set type to button
        >
          {linkUrl ? 'Save Link' : 'Remove Link'}
        </Button>
      </div>
    </div>
  );
}
